'use client';

import { formatTimestampDate } from '@/lib/date-utils';
import { Ticket } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { PostAudition } from '@kit/community';
import { auditionPath, competitionPath } from '@kit/shared/handles';
import { Badge } from '@kit/ui/badge';
import { cn } from '@kit/ui/cn';
import { Trans } from '@kit/ui/trans';

import { closesInDays, URGENCY_WINDOW_DAYS } from './post-attachments.urgency';

export function auditionHref(audition: PostAudition): string {
  if (!audition.slug) return '#';
  return audition.is_competition
    ? competitionPath(audition.slug)
    : auditionPath(audition.slug);
}

/**
 * Deadline chip/line shared by both embed variants. `overlay` renders
 * glassy pills legible on top of the cover photo; `inline` renders plain
 * text for the compact chip card.
 */
export function AuditionDeadline({
  audition,
  variant = 'inline',
  className,
}: {
  audition: PostAudition;
  variant?: 'overlay' | 'inline';
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  const days = closesInDays(audition.closingDate);
  const isClosed =
    audition.status !== 'published' || (days !== null && days < 0);
  const overlay = variant === 'overlay';

  if (isClosed) {
    return (
      <Badge
        variant="secondary"
        className={cn(
          overlay &&
            'bg-black/55 text-white ring-1 ring-white/25 backdrop-blur-sm',
          className,
        )}
      >
        <Trans i18nKey="community:feed.auditionClosed" defaults="Closed" />
      </Badge>
    );
  }

  if (days === null) return null;

  if (days <= URGENCY_WINDOW_DAYS) {
    const label =
      days === 0
        ? t('community:feed.closesToday', 'Closes today')
        : // t() so i18next plural rules apply (closesInDays_one/_other)
          t('community:feed.closesInDays', {
            count: days,
            defaultValue: 'Closes in {{count}} days',
          });
    return overlay ? (
      <Badge className={className}>{label}</Badge>
    ) : (
      <span className={cn('text-primary text-sm font-medium', className)}>
        {label}
      </span>
    );
  }

  const dateLabel = (
    <Trans
      i18nKey="community:feed.closesOn"
      defaults="Closes {{date}}"
      values={{
        date: formatTimestampDate(
          audition.closingDate,
          { month: 'short', day: 'numeric', year: 'numeric' },
          i18n.language,
        ),
      }}
    />
  );

  return overlay ? (
    <Badge
      variant="secondary"
      className={cn(
        'bg-black/55 text-white ring-1 ring-white/25 backdrop-blur-sm',
        className,
      )}
    >
      {dateLabel}
    </Badge>
  ) : (
    <span className={cn('text-muted-foreground text-sm', className)}>
      {dateLabel}
    </span>
  );
}

export function AuditionTypeBadge({ audition }: { audition: PostAudition }) {
  return (
    <Badge
      variant="secondary"
      className="bg-background/80 text-foreground backdrop-blur-sm"
    >
      <Ticket className="mr-1 h-3 w-3" aria-hidden="true" />
      {audition.is_competition ? (
        <Trans
          i18nKey="community:feed.linkedCompetition"
          defaults="Competition"
        />
      ) : (
        <Trans i18nKey="community:feed.linkedAudition" defaults="Audition" />
      )}
    </Badge>
  );
}

export function AuditionTitle({ audition }: { audition: PostAudition }) {
  return (
    <>
      {audition.title ?? (
        <Trans
          i18nKey="community:feed.untitledAudition"
          defaults="Untitled audition"
        />
      )}
    </>
  );
}
